import { format } from "@fluentui/utilities";
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import { DependencyAddBlockedReason } from "../client/permissions/IAddDependencyPermissions";
import { DependencyType } from "../service/planner/enumerations/IPlannerEnumerations";
/**
 * The default dependency constraint (basic Finish-to-Start). This is the constraint a brand-new
 * dependency gets, and the only type that does not require the "update dependency type"
 * (`IDependencyPermissions.canUpdateConstraint`) permission. Consume this everywhere the default is
 * needed instead of referencing {@link DependencyType.SourceFinishDrivesTargetStart} directly, so the
 * default can be changed in a single place.
 */ export const DefaultDependencyConstraint = DependencyType.SourceFinishDrivesTargetStart;
/**
 * Returns a map of dependency type to its localized display label.
 * Getter function ensures strings are resolved at call-time.
 */ export const getDependencyTypeLabel = ()=>({
        [DependencyType.SourceFinishDrivesTargetStart]: Strings.DependencySection_TypeFS,
        [DependencyType.SourceStartDrivesTargetStart]: Strings.DependencySection_TypeSS,
        [DependencyType.SourceFinishDrivesTargetFinish]: Strings.DependencySection_TypeFF,
        [DependencyType.TargetStartDrivesSourceFinish]: Strings.DependencySection_TypeSF
    });
/**
 * Returns a map of dependency type to its localized shorthand abbreviation (e.g. "FS", "SS").
 */ export const getDependencyTypeShorthand = ()=>({
        [DependencyType.SourceFinishDrivesTargetStart]: Strings.Dependency_FinishToStartAbbreviation,
        [DependencyType.SourceStartDrivesTargetStart]: Strings.Dependency_StartToStartAbbreviation,
        [DependencyType.SourceFinishDrivesTargetFinish]: Strings.Dependency_FinishToFinishAbbreviation,
        [DependencyType.TargetStartDrivesSourceFinish]: Strings.Dependency_StartToFinishAbbreviation
    });
/**
 * Returns a map of dependency type to its localized full name (e.g. "Finish to start").
 */ export const getDependencyTypeFullName = ()=>({
        [DependencyType.SourceFinishDrivesTargetStart]: Strings.GanttLinkLineTooltip_FinishToStart,
        [DependencyType.SourceStartDrivesTargetStart]: Strings.GanttLinkLineTooltip_StartToStart,
        [DependencyType.SourceFinishDrivesTargetFinish]: Strings.GanttLinkLineTooltip_FinishToFinish,
        [DependencyType.TargetStartDrivesSourceFinish]: Strings.GanttLinkLineTooltip_StartToFinish
    });
/**
 * Returns a map of dependency type to its localized description sentence.
 * Getter function ensures strings are resolved at call-time.
 */ export const getDependencyTypeDescription = ()=>({
        [DependencyType.SourceFinishDrivesTargetStart]: Strings.DependencySection_DescriptionFS,
        [DependencyType.SourceStartDrivesTargetStart]: Strings.DependencySection_DescriptionSS,
        [DependencyType.SourceFinishDrivesTargetFinish]: Strings.DependencySection_DescriptionFF,
        [DependencyType.TargetStartDrivesSourceFinish]: Strings.DependencySection_DescriptionSF
    });
/**
 * Returns a formatted relationship string for a dependency between two tasks.
 * @param type - The dependency type
 * @param sourceTaskName - Display name of the source task
 * @param targetTaskName - Display name of the target task
 */ export function getDependencyRelationshipText(type, sourceTaskName, targetTaskName) {
    const templates = {
        [DependencyType.SourceFinishDrivesTargetStart]: Strings.DependencySection_RelationshipFS,
        [DependencyType.SourceStartDrivesTargetStart]: Strings.DependencySection_RelationshipSS,
        [DependencyType.SourceFinishDrivesTargetFinish]: Strings.DependencySection_RelationshipFF,
        [DependencyType.TargetStartDrivesSourceFinish]: Strings.DependencySection_RelationshipSF
    };
    return format(templates[type], sourceTaskName, targetTaskName);
}
/**
 * Ordered list of dependency types for display in the picker.
 */ export const orderedDependencyTypes = [
    DependencyType.SourceFinishDrivesTargetStart,
    DependencyType.SourceStartDrivesTargetStart,
    DependencyType.SourceFinishDrivesTargetFinish,
    DependencyType.TargetStartDrivesSourceFinish
];
/**
 * Returns the localized message explaining why adding a dependency is blocked.
 * @param reason - The blocked reason
 * @param limit - The dependency limit, sent only when a per-task/per-plan limit is reached
 */ export function getDependencyAddBlockedReasonMessage(reason, limit) {
    const blockedMessages = {
        [DependencyAddBlockedReason.MissingPremiumLicense]: Strings.DependencySection_AddBlockedMissingLicense,
        [DependencyAddBlockedReason.MissingStartAndFinishDates]: Strings.DependencySection_AddBlockedMissingStartAndFinishDates,
        [DependencyAddBlockedReason.MissingStartDate]: Strings.DependencySection_AddBlockedMissingStartDate,
        [DependencyAddBlockedReason.MissingFinishDate]: Strings.DependencySection_AddBlockedMissingFinishDate,
        [DependencyAddBlockedReason.TaskLimitReached]: Strings.DependencySection_AddBlockedTaskLimitReached,
        [DependencyAddBlockedReason.PlanLimitReached]: Strings.DependencySection_AddBlockedPlanLimitReached,
        [DependencyAddBlockedReason.CreatesCircularDependency]: Strings.DependencySection_AddBlockedCreatesCircularDependency
    };
    return format(blockedMessages[reason], limit);
}
